import { CalendarDaysIcon, ChevronDown, ClipboardListIcon, Plus } from 'assets/icons';
import Button from 'components/button';
import MenuButton from 'components/DropdownMenu/MenuButton';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import StyledInput from 'components/styledInput/StyledInput';
import PillTabs from 'components/tabs/PillTabs';
import useText from 'hooks/useText';
import { useState } from 'react';
import ActivityEntry from './ActivityEntry';
import { ACTIVITY_FILTERS, activitySections, countByFilter } from './activityItems';

/**
 * The « Activité » of a record (activityItems.js builds `items`): a filter by
 * kind with its counts, what is to come, then the past by month. `sources`
 * (a group): [{ id, label }] and `allLabel`, a menu picks whose activity to
 * read. « Ajouter » asks what to add, a task or an appointment, then opens
 * its form (`onAdd('task' | 'event')`).
 */
const RecordActivity = ({ items, sources, allLabel, onAdd, onOpen }) => {
  const { text } = useText('recordActivity');
  const [filter, setFilter] = useState('all');
  const [source, setSource] = useState('all');
  const scoped = source === 'all' ? items : items.filter((item) => item.source === source);
  const hasClient = scoped.some((item) => item.kind === 'client');
  // « Actions clients » picked, then gone (no action of the client left): back to « Tout ».
  const active = filter === 'client' && !hasClient ? 'all' : filter;
  const counts = countByFilter(scoped);
  const { count, upcoming, months } = activitySections(scoped, active);

  return (
    <div className="flex flex-col gap-4">
      {/* The filters keep their width: the menu and « Ajouter » go to the
          next line when they lack room. */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="shrink-0">
          <PillTabs
            activeTab={active}
            onChange={setFilter}
            tabs={ACTIVITY_FILTERS.filter((id) => id !== 'client' || hasClient).map((id) => ({
              id,
              label: text(`filter_${id}`),
              count: id === 'all' ? undefined : counts[id],
            }))}
          />
        </div>
        <span className="flex-1" />
        {sources && (
          <div className="w-64">
            <StyledInput
              type="select"
              value={source}
              list={[{ value: 'all', label: allLabel }, ...sources.map((one) => ({ value: one.id, label: one.label }))]}
              onChange={(event) => setSource(event.target.value)}
            />
          </div>
        )}
        {active !== 'client' && (
          <MenuButton
            trigger={(toggleMenu) => (
              <Button color="SECONDARY" size="SM" className="shrink-0" onClick={toggleMenu}>
                <Plus wh={18} color="currentColor" />
                <span>{text('add')}</span>
                <ChevronDown wh={16} color="currentColor" />
              </Button>
            )}
            items={[
              { label: text('addTask'), icon: ClipboardListIcon, onClick: () => onAdd('task') },
              { label: text('addEvent'), icon: CalendarDaysIcon, onClick: () => onAdd('event') },
            ]}
          />
        )}
      </div>
      {!count && (
        <CompactEmptyState compact glyph="inbox" title={text(`empty_${active}`)} description={text(`emptyText_${active}`)} />
      )}
      {upcoming.length > 0 && (
        <div>
          <p className="mb-3 text-sm font-medium text-gray-900">{text('upcoming')}</p>
          {upcoming.map((item) => (
            <ActivityEntry key={item.key} item={item} onOpen={onOpen} />
          ))}
        </div>
      )}
      {months.map((month) => (
        <div key={month.label}>
          <p className="mb-3 text-sm font-medium text-gray-900">{month.label}</p>
          {month.items.map((item) => (
            <ActivityEntry key={item.key} item={item} onOpen={onOpen} />
          ))}
        </div>
      ))}
    </div>
  );
};

export default RecordActivity;
